import { useEffect, useId, useRef, useState, type FormEvent, type ReactNode } from "react";
import type { CallerNumberRow, CallerNumbersResponse, TeamMember } from "@gioia365/contracts";
import { useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { ApiRequestError } from "../../api/http";
import { Button } from "../../design/components/Button";
import { Chip } from "../../design/components/Chip";
import { Dialog } from "../../design/components/Dialog";
import { Icon } from "../../design/components/Icon";
import { Reason } from "../../design/components/Reason";
import { Select } from "../../design/components/Select";
import { StatusPill } from "../../design/components/StatusPill";
import { useToast } from "../../design/components/Toast";
import { messageForError } from "../../i18n/errors";
import { useLocale, useT, type Translate } from "../../i18n/locale";
import { withLink } from "../../i18n/with-link";
import { formatPhoneForDisplay } from "../../leads/lead-display";
import type { TabContext } from "../../session/tab-context";
import { callerNumbersQuery, numberOf, useAssignCallerNumber, useImportCallerNumbers, useRemoveCallerNumber } from "../../telephony/caller-number-queries";
import { AddCallerNumberDialog } from "./add-caller-number-dialog";
import "./caller-numbers.css";

// Il parco dei cellulari dell'ufficio in Impostazioni → Team (piano M4, Task 17, mock-up `mockup.js`, Team): la cella
// «Numero per chiamare» di ogni persona, il riepilogo sotto il titolo e la sezione «Parco numeri per chiamare» sotto la tabella.
// Ognuno ha un numero, e lo stesso numero può essere di più persone (TEL-3, 18/10/2026): il parco dice tutte le persone di un
// numero, e la scelta offre anche quelli dei colleghi («anche di …»).
// Si vedono soltanto se il server lascia leggere il parco (chi gestisce il team o ha la sezione «Numeri per chiamare»; la
// piattaforma sempre). I cellulari si danno alle persone, con la sezione, da «Gestisci» (`team-dialogs.tsx`) e, sulle righe
// che non hanno «Gestisci» (il titolare, la propria, chi non si gestisce), dalla cella «Numero per chiamare»
// (`CallerNumberDialog`), con la stessa scelta (`useNumberChoice`): il server lo permette a ogni persona attiva (revisione
// finale del Task 17). Aggiungerli, leggere quelli già verificati presso il fornitore, verificarli di nuovo e toglierli dal parco
// lo fa chi ha «Numeri per chiamare» (il Super admin e l'Admin dell'azienda, TEL-3 del 21/10/2026) e il Super admin di Gioia365,
// mai in «Guarda come…» (`canAdd`, dal server). Il server controlla comunque ogni richiesta.

/**
 * Il «Numero per chiamare» di una persona: il numero come si legge e, sotto, il nome del cellulare; senza, il segno vuoto.
 * `action` è il controllo del solo numero, per le righe senza «Gestisci».
 */
export function CallerNumberCell({ row, action }: { row: CallerNumberRow | null; action?: ReactNode }) {
  const t = useT();
  const control = action ? <span className="g-callers__cell-action">{action}</span> : null;
  if (row === null) {
    return (
      <>
        <span className="g-callers__none" aria-hidden="true">—</span>
        <span className="g-visually-hidden">{t("callerNumbers.none")}</span>
        {control}
      </>
    );
  }
  return (
    <>
      <span className="g-callers__number">{formatPhoneForDisplay(row.e164)}</span>
      {row.label !== null ? <span className="g-table__sub">{row.label}</span> : null}
      {control}
    </>
  );
}

/** «Numeri per chiamare: 2 assegnati su 3.»: quanti dei cellulari verificati, quelli che si possono dare, sono di qualcuno. */
export function poolSummary(t: Translate, pool: CallerNumbersResponse): string {
  const verified = pool.numbers.filter((row) => row.state === "verified");
  const assigned = verified.filter((row) => row.assignees.length > 0).length;
  return assigned === 1 ? t("callerNumbers.summaryOne", { total: verified.length }) : t("callerNumbers.summary", { assigned, total: verified.length });
}

/** I nomi delle persone di un cellulare, senza `except`, come si leggono nella lingua della pagina: «Desirè e Lucio Gervasi». */
export function assigneeNames(row: CallerNumberRow, locale: string, except?: string): string {
  const names = row.assignees.filter((person) => person.membershipId !== except).map((person) => person.displayName);
  return new Intl.ListFormat(locale, { type: "conjunction" }).format(names);
}

/**
 * I cellulari da offrire a una persona in «Gestisci»: il suo e, soltanto a chi è attivo, tutti gli altri verificati, liberi o
 * già di colleghi (TEL-3: lo stesso numero può essere di più persone). Il server dà un cellulare soltanto a una persona attiva;
 * chi è sospeso tiene il suo. Mai uno in verifica o non riuscito.
 */
export function offeredNumbers(pool: CallerNumbersResponse, member: TeamMember): CallerNumberRow[] {
  return pool.numbers.filter(
    (row) => row.state === "verified" && (member.status === "active" || row.assignees.some((person) => person.membershipId === member.membershipId)),
  );
}

/**
 * Una voce della scelta per una persona, come nel mock-up: «+39 300 000 0101 · Postazione 1», con « · libero» se non è di nessuno
 * e « · anche di Desirè» se è già di altre persone.
 */
export function numberOptionLabel(t: Translate, locale: string, row: CallerNumberRow, membershipId: string): string {
  const others = assigneeNames(row, locale, membershipId);
  const holders = row.assignees.length === 0 ? t("callerNumbers.free") : others === "" ? null : t("callerNumbers.alsoOf", { names: others });
  const parts = [formatPhoneForDisplay(row.e164), row.label, holders];
  return parts.filter((part): part is string => part !== null).join(" · ");
}

/** Perché la scelta del numero è spenta: al ruolo di chi gestisce manca la sezione, con il collegamento a «Ruoli e permessi». */
export function AssignBlockedHint() {
  const t = useT();
  return withLink(t("callerNumbers.assign.blocked"), <Link to="/impostazioni/ruoli-e-permessi">{t("settings.roles")}</Link>);
}

/**
 * La scelta del «Numero per chiamare» di una persona, una sola per «Gestisci» (`MemberDialog`) e per la finestra del solo
 * numero (`CallerNumberDialog`). La scelta c'è se il parco si legge e la persona non è archiviata; si cambia con la sezione
 * «Numeri per chiamare» (`canAssign`), altrimenti resta spenta con il perché scritto. Si offrono il numero della persona e, a
 * chi è attivo, gli altri verificati, anche quelli dei colleghi (`offeredNumbers`). Finché non si sceglie, la scelta segue il
 * numero che la persona ha nel parco letto (anche se il parco arriva dopo l'apertura), così salvare il resto non tocca il
 * numero; una scelta che il parco riletto non offre più (tolta dal parco) torna al numero che la persona ha adesso.
 *
 * `save` toglie alla persona il numero che ha (soltanto a lei: chi lo condivide lo tiene) e le dà quello scelto, ognuno con la
 * sua versione: una persona ne ha uno solo. Se il server rifiuta (un collega l'ha cambiato, `409 version_conflict`; la persona
 * ne ha già un altro, o il cellulare non è verificato) il parco si rilegge, la scelta riparte da ciò che il server ha adesso e
 * l'errore sta sul campo, con il fuoco; se il vecchio era già stato tolto, l'errore dice che la persona l'ha perso.
 */
export function useNumberChoice(tab: TabContext, pool: CallerNumbersResponse | null, member: TeamMember) {
  const t = useT();
  const { locale } = useLocale();
  const assign = useAssignCallerNumber(tab);
  const queryClient = useQueryClient();
  const [chosen, setChosen] = useState<string | null>(null);
  // Quante volte la scelta ha chiesto il fuoco, dopo un rifiuto: lo riporta `CallerNumberSelect`, che ha l'elemento.
  const [focusRequest, setFocusRequest] = useState(0);
  const [problem, setProblem] = useState<((tr: Translate) => string) | null>(null);
  const shown = pool !== null && member.status !== "archived";
  const current = pool ? numberOf(pool, member.membershipId) : null;
  const offered = pool ? offeredNumbers(pool, member) : [];
  const currentId = current?.id ?? "";
  const value = chosen === null || (chosen !== "" && !offered.some((row) => row.id === chosen)) ? currentId : chosen;
  const canAssign = shown && pool.canAssign;

  async function save(name: string): Promise<boolean> {
    const next = offered.find((row) => row.id === value) ?? null;
    let released: CallerNumberRow | null = null;
    try {
      if (current !== null) {
        await assign.mutateAsync({ id: current.id, body: { membershipId: member.membershipId, assigned: false, expectedVersion: current.version } });
        released = current;
      }
      if (next !== null) await assign.mutateAsync({ id: next.id, body: { membershipId: member.membershipId, assigned: true, expectedVersion: next.version } });
      return true;
    } catch (error) {
      const conflict = error instanceof ApiRequestError && error.code === "version_conflict";
      const reason = (tr: Translate) => (conflict ? tr("callerNumbers.assign.versionConflict") : messageForError(tr, error));
      const lost = released;
      // Il parco com'è adesso (riletto prima di dirlo): la scelta riparte dal numero che la persona ha, e il campo dice perché.
      await queryClient.fetchQuery({ ...callerNumbersQuery(tab), staleTime: 0 }).catch(() => null);
      setChosen(null);
      setProblem(() => (tr: Translate) => (lost ? tr("callerNumbers.assign.releasedOnly", { name, number: formatPhoneForDisplay(lost.e164), reason: reason(tr) }) : reason(tr)));
      setFocusRequest((count) => count + 1);
      return false;
    }
  }

  let hint: ReactNode = undefined;
  if (shown && !pool.canAssign) hint = <AssignBlockedHint />;
  else if (shown && member.status !== "active") hint = t("callerNumbers.assign.suspended");

  return {
    /** C'è la scelta: il parco si legge e la persona non è archiviata. */
    shown,
    canAssign,
    /** La scelta è diversa dal numero che la persona ha, e chi guarda la può salvare. */
    changed: canAssign && value !== currentId,
    pending: assign.isPending,
    clearProblem: () => setProblem(null),
    save,
    field: {
      focusRequest,
      value,
      options: [{ value: "", label: t("callerNumbers.none") }, ...offered.map((row) => ({ value: row.id, label: numberOptionLabel(t, locale, row, member.membershipId) }))],
      disabled: !canAssign,
      hint,
      error: problem === null ? undefined : problem(t),
      onChange: (next: string) => {
        setChosen(next);
        setProblem(null);
      },
    },
  };
}

export type NumberChoice = ReturnType<typeof useNumberChoice>;

/**
 * La scelta «Numero per chiamare» di `useNumberChoice`, con il suo perché quando è spenta e l'errore del server; dopo un rifiuto
 * il fuoco torna qui.
 */
export function CallerNumberSelect({ id, choice }: { id: string; choice: NumberChoice }) {
  const t = useT();
  const { field } = choice;
  const ref = useRef<HTMLSelectElement>(null);
  useEffect(() => {
    if (field.focusRequest > 0) ref.current?.focus();
  }, [field.focusRequest]);
  return (
    <Select
      id={id}
      ref={ref}
      label={t("callerNumbers.column")}
      value={field.value}
      onChange={field.onChange}
      options={field.options}
      disabled={field.disabled}
      hint={field.hint}
      error={field.error}
    />
  );
}

type NumberDialogProps = {
  tab: TabContext;
  member: TeamMember;
  pool: CallerNumbersResponse;
  onClose: () => void;
  onSaved: (name: string) => void;
};

/**
 * Il solo «Numero per chiamare» di una persona, dalla sua cella, per chi ha la sezione «Numeri per chiamare» sulle righe che
 * non hanno «Gestisci»: il titolare, protetto per tutti, la propria riga e le persone che chi guarda non gestisce (revisione
 * finale del Task 17). La scelta e il salvataggio sono quelli di «Gestisci» (`useNumberChoice`); senza cambiamenti «Salva» chiude.
 */
export function CallerNumberDialog({ tab, member, pool, onClose, onSaved }: NumberDialogProps) {
  const t = useT();
  const formId = useId();
  const number = useNumberChoice(tab, pool, member);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (number.pending) return;
    if (!number.changed) {
      onClose();
      return;
    }
    number.clearProblem();
    if (await number.save(member.displayName)) onSaved(member.displayName);
  }

  return (
    <Dialog
      open
      title={t("callerNumbers.numberDialog.title", { name: member.displayName })}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>{t("common.cancel")}</Button>
          <Button variant="primary" type="submit" form={formId} disabled={number.pending} aria-busy={number.pending ? "true" : undefined}>
            {t("team.manageDialog.save")}
          </Button>
        </>
      }
    >
      <form id={formId} className="g-stack" onSubmit={(event) => void handleSubmit(event)} aria-busy={number.pending} noValidate>
        <CallerNumberSelect id={`${formId}-number`} choice={number} />
      </form>
    </Dialog>
  );
}

/**
 * Se la riga di una persona ha il controllo del solo numero: chi guarda può dare i numeri (`canAssign`) ma su questa riga non
 * ha «Gestisci», e c'è qualcosa da fare. Mai per chi è archiviato; per chi è sospeso soltanto se ha un numero, che può lasciare
 * (non ne riceve uno nuovo).
 */
export function hasNumberControl(pool: CallerNumbersResponse | null, member: TeamMember, manageable: boolean): boolean {
  if (pool === null || !pool.canAssign || manageable || member.status === "archived") return false;
  return member.status === "active" || numberOf(pool, member.membershipId) !== null;
}

type Panel = { kind: "add" } | { kind: "verify"; row: CallerNumberRow } | { kind: "remove"; row: CallerNumberRow };

type SectionProps = {
  tab: TabContext;
  /** Il parco letto; `null` finché non si è letto (allora c'è soltanto il problema, con «Riprova»). */
  pool: CallerNumbersResponse | null;
  /** La lettura non è riuscita: la sezione lo dice, con «Riprova». */
  failed?: { retry: () => void; retrying: boolean };
};

/** «Parco numeri per chiamare»: un cellulare per riga, con il nome, di chi è (tutte le persone) o se è libero, e lo stato della verifica. */
export function CallerNumbersSection({ tab, pool, failed }: SectionProps) {
  const t = useT();
  const toast = useToast();
  const titleId = useId();
  const headingRef = useRef<HTMLHeadingElement>(null);
  const importNumbers = useImportCallerNumbers(tab);
  const remove = useRemoveCallerNumber(tab);
  const [panel, setPanel] = useState<Panel | null>(null);
  const canAdd = pool?.canAdd ?? false;

  // Come le azioni di stato del Team: l'avviso dice com'è andata (D16). Quanti sono nuovi si conta sull'elenco di prima.
  function readVerified() {
    if (pool === null || importNumbers.isPending) return;
    const before = new Set(pool.numbers.map((row) => row.id));
    void importNumbers.mutateAsync().then(
      (next) => {
        const added = next.numbers.filter((row) => !before.has(row.id)).length;
        if (added === 0) toast.show(t("callerNumbers.importedNone"));
        else toast.show(added === 1 ? t("callerNumbers.importedOne") : t("callerNumbers.imported", { count: added }), "success");
      },
      (error: unknown) => toast.show(messageForError(t, error), "danger"),
    );
  }

  // La conferma si chiude prima, poi l'avviso; il pulsante premuto sparisce con la riga, quindi il fuoco va sul titolo.
  function removeConfirmed(row: CallerNumberRow) {
    setPanel(null);
    const number = formatPhoneForDisplay(row.e164);
    void remove.mutateAsync(row.id).then(
      () => {
        toast.show(t("callerNumbers.removeDialog.done", { number }), "success");
        headingRef.current?.focus();
      },
      (error: unknown) => toast.show(messageForError(t, error), "danger"),
    );
  }

  let list: ReactNode = null;
  if (pool !== null && pool.numbers.length === 0) list = <p className="g-team__counts">{t("callerNumbers.empty")}</p>;
  else if (pool !== null) {
    list = (
      <ul className="g-callers__list">
        {pool.numbers.map((row) => (
          <PoolItem
            key={row.id}
            row={row}
            canAdd={canAdd}
            onVerify={() => setPanel({ kind: "verify", row })}
            onRemove={() => setPanel({ kind: "remove", row })}
          />
        ))}
      </ul>
    );
  }

  return (
    <section className="g-team g-callers" aria-labelledby={titleId}>
      <div className="g-team__head">
        <div className="g-team__title">
          <h2 id={titleId} ref={headingRef} tabIndex={-1}>
            {t("callerNumbers.poolTitle")}
          </h2>
          <p className="g-team__counts">{t("callerNumbers.poolHint")}</p>
        </div>
        {canAdd ? (
          <div className="g-callers__actions">
            <Button onClick={readVerified} disabled={importNumbers.isPending} aria-busy={importNumbers.isPending ? "true" : undefined}>
              {t("callerNumbers.import")}
            </Button>
            <Button variant="primary" onClick={() => setPanel({ kind: "add" })}>
              <Icon name="plus" />
              {t("callerNumbers.add")}
            </Button>
          </div>
        ) : null}
      </div>
      {failed ? (
        <div className="g-team__problem">
          <Reason tone="warning" role="alert">
            {t("callerNumbers.loadFailed")}
          </Reason>
          <Button onClick={failed.retry} disabled={failed.retrying} aria-busy={failed.retrying ? "true" : undefined}>
            {t("common.retry")}
          </Button>
        </div>
      ) : null}
      {list}

      {panel?.kind === "add" ? <AddCallerNumberDialog tab={tab} existing={null} onClose={() => setPanel(null)} /> : null}
      {panel?.kind === "verify" ? <AddCallerNumberDialog tab={tab} existing={panel.row} onClose={() => setPanel(null)} /> : null}
      {panel?.kind === "remove" ? <RemoveDialog row={panel.row} onCancel={() => setPanel(null)} onConfirm={() => removeConfirmed(panel.row)} /> : null}
    </section>
  );
}

type ItemProps = { row: CallerNumberRow; canAdd: boolean; onVerify: () => void; onRemove: () => void };

function PoolItem({ row, canAdd, onVerify, onRemove }: ItemProps) {
  const t = useT();
  const { locale } = useLocale();
  const number = formatPhoneForDisplay(row.e164);
  let state: ReactNode;
  if (row.state === "verified") {
    state =
      row.assignees.length > 0 ? (
        <span className="g-callers__owner">{t("callerNumbers.of", { name: assigneeNames(row, locale) })}</span>
      ) : (
        <StatusPill tone="success" icon="check">
          {t("callerNumbers.free")}
        </StatusPill>
      );
  } else if (row.state === "requested") {
    // Chi può verificare legge la frase della verifica, che rimanda a «Verifica» sulla stessa riga; gli altri soltanto l'attesa.
    state = (
      <span className="g-callers__state">
        <Icon name="clock" />
        {canAdd ? t("callerVerification.requested") : t("callerNumbers.requested")}
      </span>
    );
  } else {
    // Non riuscita: «riprova» soltanto a chi può riprovare; agli altri chi lo fa.
    state = (
      <span className="g-callers__state" data-tone="warning">
        <Icon name="alert" />
        {canAdd ? t("callerNumbers.failed") : t("callerNumbers.failedElsewhere")}
      </span>
    );
  }
  return (
    <li className="g-callers__item">
      <span className="g-callers__what">
        <strong className="g-callers__number">{number}</strong>
        {row.label !== null ? <Chip>{row.label}</Chip> : null}
        {state}
      </span>
      {canAdd ? (
        <span className="g-callers__actions">
          {row.state === "verified" ? null : (
            <Button variant="ghost" onClick={onVerify} aria-label={t("callerNumbers.verifyNamed", { number })}>
              {t("callerVerification.verify")}
            </Button>
          )}
          <Button variant="ghost" onClick={onRemove} aria-label={t("callerNumbers.removeNamed", { number })}>
            {t("callerNumbers.remove")}
          </Button>
        </span>
      ) : null}
    </li>
  );
}

type RemoveProps = { row: CallerNumberRow; onCancel: () => void; onConfirm: () => void };

/** La conferma prima di togliere un cellulare dal parco: chi lo perde (tutte le persone), e che le chiamate passate restano com'erano. */
function RemoveDialog({ row, onCancel, onConfirm }: RemoveProps) {
  const t = useT();
  const { locale } = useLocale();
  let who = t("callerNumbers.removeDialog.free");
  if (row.assignees.length === 1) who = t("callerNumbers.removeDialog.assigned", { name: assigneeNames(row, locale) });
  else if (row.assignees.length > 1) who = t("callerNumbers.removeDialog.assignedMany", { names: assigneeNames(row, locale) });
  return (
    <Dialog
      open
      title={t("callerNumbers.removeDialog.title", { number: formatPhoneForDisplay(row.e164) })}
      onClose={onCancel}
      footer={
        <>
          <Button onClick={onCancel}>{t("common.cancel")}</Button>
          <Button variant="primary" onClick={onConfirm}>
            {t("callerNumbers.remove")}
          </Button>
        </>
      }
    >
      <div className="g-stack">
        <p>{who}</p>
        <p>{t("callerNumbers.removeDialog.past")}</p>
      </div>
    </Dialog>
  );
}
